import React, { useState, useEffect, useContext, useMemo } from "react"; // Import useMemo
import { useNavigate, useParams } from "react-router-dom";
import { CSSTransition } from "react-transition-group";
import UserContext from "../../../UserContext";
import { PreviewContext } from "../../../PreviewContext";
import { HeaderContext } from "../../../HeaderContext";
import Text from "../../components/Text";
import { URLSNAV } from "../../constants/urls";
import { API_URLS } from "../../../config";
import "./styles/LowRatingPage.css";
import RatingQuestion from "../../components/RatingQuestion";
import { BrandingContext } from "../../../BrandingContext"; // Import BrandingContext

const LowRatingPage = () => {
  const { setHeaderAnimated, setHeaderExtended } = useContext(HeaderContext);
  const { isPreview, accentColor: previewAccentColor, customQuestions } = useContext(PreviewContext);
  const { accentColor } = useContext(BrandingContext); // Get accentColor from BrandingContext
  const navigate = useNavigate();
  const { bulkData, setBulkData, token: jwtToken } = useContext(UserContext);
  const { id } = useParams();

  console.log("LowRatingPage: isPreview =", isPreview);
  console.log("LowRatingPage: customQuestions from context =", customQuestions);

  // Memoize initialQuestionsState
  const initialQuestionsState = useMemo(() => {
    if (isPreview) {
      console.log("LowRatingPage (useMemo): Using customQuestions from PreviewContext");
      return Array.isArray(customQuestions) ? customQuestions : [];
    }

    const dataFromBulk = bulkData[id]?.questions?.map(q => ({
      id: q.questionId,
      text: q.text || "Loading question...",
      rating: q.rating || 0,
      show: false,
      topic_name: q.topicName,
      topic_id: q.topicId
    })) || [];
    console.log("LowRatingPage (useMemo): Using dataFromBulk", dataFromBulk);
    return dataFromBulk.length > 0 ? dataFromBulk : [];
  }, [isPreview, customQuestions, bulkData[id]?.questions]);

  console.log("LowRatingPage: Initial questions state (memoized) =", initialQuestionsState);

  const [questions, setQuestions] = useState([]);
  const [showQuestions, setShowQuestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false); // Initialize isLoading to false
  const [disableButton, setDisableButton] = useState(true); // Will be updated by effects

  // Effect 1: Sync questions from initialQuestionsState, manage loading state for API calls
  useEffect(() => {
    console.log("LowRatingPage Effect 1: Syncing with initialQuestionsState", initialQuestionsState);
    const processedQuestions = Array.isArray(initialQuestionsState)
      ? initialQuestionsState.map(q => ({ ...q, show: q.show !== undefined ? q.show : false }))
      : [];

    setQuestions(processedQuestions);
    setShowQuestions(false); // Reset animation trigger, Effect 3 will turn it on
    setDisableButton(processedQuestions.length === 0 || processedQuestions.some(q => q.rating === 0));

    // Determine if API loading is needed (only if not in preview and no questions from bulkData and not yet sent)
    if (!isPreview && processedQuestions.length === 0 && !(bulkData[id]?.isSent)) {
      console.log("LowRatingPage Effect 1: Triggering API load by setting isLoading to true");
      setIsLoading(true);
    } else {
      setIsLoading(false); // Not loading if in preview, or questions exist from bulkData, or survey already sent
    }
  }, [initialQuestionsState, isPreview, id, bulkData]); // Dependencies for syncing and deciding to load

  // Effect 2: API fetching and redirection logic
  useEffect(() => {
    setHeaderAnimated(false);
    setHeaderExtended(false);

    // Handle redirection if survey already sent (non-preview)
    if (!isPreview && bulkData[id] && bulkData[id].isSent) {
      if (bulkData[id].generalRating > 3) {
        navigate(URLSNAV.HIGH_FEEDBACK(id));
      } else {
        navigate(URLSNAV.LOW_THANX(id));
      }
      return; // Stop further execution in this effect
    }

    const fetchQuestionsFromAPI = async () => {
      // Only fetch if not in preview and isLoading is true (set by Effect 1)
      if (!isPreview && isLoading) {
        try {
          console.log("LowRatingPage Effect 2: Fetching questions from API");
          const response = await fetch(API_URLS.questions(id), {
            headers: { 'Authorization': `Bearer ${jwtToken}` }
          });
          if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
          const data = await response.json();
          console.log("LowRatingPage Effect 2: Fetched questions data: ", data);
          const fetchedQuestions = data.map(q => ({
            id: q.question_id,
            text: q.question_text,
            rating: 0,
            show: false, // Prepare for animation, Effect 3 will handle showing
            topic_name: q.topic_name,
            topic_id: q.topic_id
          }));
          
          setQuestions(fetchedQuestions); // Update questions state with API data
          setDisableButton(fetchedQuestions.length === 0 || fetchedQuestions.some(q => q.rating === 0));
        } catch (error) {
          console.error("LowRatingPage Effect 2: Error fetching questions:", error);
          setQuestions([]); // Clear questions on error
          setDisableButton(true);
        } finally {
          setIsLoading(false); // Ensure isLoading is set to false after fetch attempt
        }
      }
    };

    fetchQuestionsFromAPI();
  }, [isPreview, isLoading, id, jwtToken, navigate, setHeaderAnimated, setHeaderExtended, bulkData]); // Dependencies for API calls and navigation

  // Effect 3: Trigger question visibility animation
  useEffect(() => {
    console.log("LowRatingPage Effect 3: Checking to show questions. Count:", questions.length, "Current showQuestions:", showQuestions);
    if (questions.length > 0) {
      // Effect 1 sets showQuestions to false. This effect will set it to true.
      const timer = setTimeout(() => {
        console.log("LowRatingPage Effect 3: Setting showQuestions to true");
        setShowQuestions(true);
      }, 50); // Small delay for CSS transition
      return () => clearTimeout(timer); // Cleanup timer
    } else {
      // If there are no questions (e.g., after an error or initially), ensure they are not shown.
      // Effect 1 also handles this, but this provides an additional safeguard.
      setShowQuestions(false);
    }
  }, [questions]); // This effect runs whenever the `questions` state array changes

  const isAnyQuestionUnrated = () => questions.some(q => q.rating === 0);

  const handleSubmit = (evt) => {
    evt.preventDefault();

    const newQuestions = questions.map(question => {
      if (question.rating === 0) {
        return { ...question, errorMessage: "Por favor, selecciona al menos una estrella." };
      } else {
        return { ...question, errorMessage: "" };
      }
    });

    setQuestions(newQuestions);

    if (isAnyQuestionUnrated()) {
      return;
    }

    if (!isPreview) {
        console.log("LowRatingPage: Updating bulkData with question ratings");
        const bulkQuestions = questions.map(q => ({
          questionId: q.id,
          rating: q.rating,
          topicName: q.topic_name,
          topicId: q.topic_id,
          text: q.text
        }));

        setBulkData(prevData => ({
          ...prevData,
          [id]: {
            ...(prevData[id] || {}),
            questions: bulkQuestions,
            lastPage: URLSNAV.LOW_RATING(id),
          }
        }));
    } else {
      console.log("LowRatingPage: In preview mode - skipping bulkData update");
    }

    goToNextPage();
  };

  const setRatingQ = (questionId) => (rating) => {
    setQuestions(prevQuestions => {
      const updatedQuestions = prevQuestions.map(q => q.id === questionId ? { ...q, rating, errorMessage: "" } : q);
      const isUnrated = updatedQuestions.some(q => q.rating === 0);
      setDisableButton(isUnrated);
      return updatedQuestions;
    });
  };

  const goToNextPage = () => {
    // Use the proper state for controlling CSS transitions
    setShowQuestions(false);

    if (isPreview) {
       console.log("LowRatingPage: In preview mode - navigation skipped");
       return;
    }

    console.log("LowRatingPage: Navigating to feedback page");
    setTimeout(() => {
      navigate(URLSNAV.LOW_FEEDBACK(id));
    }, 400);
  };

  const currentAccentColor = isPreview ? previewAccentColor : accentColor;
  console.log("LowRatingPage: Current accent color:", currentAccentColor);
  const buttonStyle = {
    background: disableButton ? '#ccc' : (currentAccentColor || '#007bff'),
    color: '#ffffff',
  };

  return (
    <form onSubmit={handleSubmit} className="review-form">
      {isLoading ? (
        <div className="loading">
          <Text className="loading-text" content="Loading questions..." />
        </div>
      ) : questions.length === 0 ? (
         <div className="loading">
           <Text className="loading-text" content="There are no available questions." />
         </div>
      ) : (
        <>
          {questions.map(question => (
            <CSSTransition
              key={question.id}
              in={showQuestions}
              timeout={400}
              classNames="fade-in-up"
              unmountOnExit
            >
              <div className="rating-container">
                <RatingQuestion
                   mainText={question.text}
                   setRating={setRatingQ(question.id)}
                   initialRating={question.rating}
                 />
                {question.errorMessage && <div className="error-message">{question.errorMessage}</div>}
              </div>
            </CSSTransition>
          ))}
          <CSSTransition
            key={"submit"}
            in={showQuestions}
            timeout={400}
            classNames="fade-in-up"
            unmountOnExit
          >
            <div className="rating-container button-container">
              <input
                type="submit"
                className={`submit-button${disableButton ? " disabled" : ""}`}
                value="Submit review"
                disabled={disableButton}
                style={buttonStyle}
              />
            </div>
          </CSSTransition>
        </>
      )}
    </form>
  );
};

export default LowRatingPage;